import { Dispatch } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { prefetchDesignVariations } from '@vp/quick-view-modal/prefetch'

import { designVariationsInputSelector } from '~/app/store/quickView/selectors'
import { selectQuickViewFilterNames } from '~/app/store/quickViewFilter'
import { DESIGN_VARIATIONS_QUERY_KEY_NAME, DESIGN_VARIATIONS_QUERY_SETTINGS } from '~/app/features/Gallery/hooks/useDesignVariations'
import { queryDesignVariations } from '~/app/features/Gallery/queries/queryDesignVariations'
import { ThunkDispatch } from '~/app/store/interface'
import { useFeatureGate } from '~/app/features/Gallery/hooks/useFeatureGate'
import { FEATURE_GATES } from 'shared/featureGates/constants'
import type { DesignVariations } from 'shared/types/gallery/design-variations'
import type { State } from 'shared/types/gallery/state'

export const usePrefetchQuery = (tileEntity: State.TileEntity): (() => void) | undefined => {
  const dispatch = useDispatch<ThunkDispatch>()
  const queryClient = useQueryClient()

  const quickViewFilters = useSelector(selectQuickViewFilterNames)
  const showNewQuickViewModal = useFeatureGate(FEATURE_GATES.GALLERY_QUICK_VIEW_MODAL_ENABLED)
  const hasOldQVPrefetch = quickViewFilters.length > 0

  if (!hasOldQVPrefetch && !showNewQuickViewModal) {
    return undefined
  }

  return (): void => {
    // Access state bypassing useSelector to prevent unnesesary re-renders after refinement changes
    dispatch((_: Dispatch, getState: () => State.GlobalState) => {
      const { productOptions, taxonomyVariants } = tileEntity
      const designVariationsInput = designVariationsInputSelector(getState())(tileEntity, productOptions, Object.keys(taxonomyVariants))
      const {
        locale,
        productKey,
        mpvId,
        selectedOptions,
        variableOptions,
        variableAttributes,
        variableTaxonomyIds,
        designCreationTypes,
        designPersonalizationContextId,
        templateToken,
        scenePurpose,
      } = designVariationsInput

      if (showNewQuickViewModal) {
        prefetchDesignVariations({
          designConceptId: templateToken,
          locale,
          productKey,
          mpvId,
          selectedOptions,
          variableOptions,
          variableAttributes,
          variableTaxonomyIds,
          designCreationTypes: designCreationTypes ?? undefined,
          designPersonalizationContextId: designPersonalizationContextId ?? undefined,
          scenePurpose,
        })
      } else if (hasOldQVPrefetch) {
        const dvQueryKey: DesignVariations.QueryKey = [DESIGN_VARIATIONS_QUERY_KEY_NAME, designVariationsInput]

        queryClient.prefetchQuery({ queryKey: dvQueryKey, queryFn: queryDesignVariations, ...DESIGN_VARIATIONS_QUERY_SETTINGS })
      }
    })
  }
}
